/**
 * Side sheet displaying detailed task information.
 */
import { type FC, useEffect } from "react";

import type { TaskDetailsResult } from "../../hooks/useTaskDetails";
import type { TaskLogsResult } from "../../hooks/useTaskLogs";
import type { TaskSnapshot } from "../../types";

import { useProviderProgress } from "../../hooks/useProviderProgress";
import { useRealtimeTaskUsage } from "../../hooks/useRealtimeTaskUsage";
import { useSchemaTelemetryScopeSummary } from "../../hooks/useSchemaTelemetryScopeSummary";
import { formatRelativeTime } from "../../utils/time";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { LoadingState } from "../common/LoadingState";
import { SideSheet } from "../common/SideSheet";
import { StatusBadge } from "../common/StatusBadge";
import { Text } from "../common/Text";
import { ValidationFailureSection } from "../ValidationFailureSection";
import { STATUS_LABELS } from "./constants";
import { LiveUsageMetrics } from "./LiveUsageMetrics";
import { PlanRevisionCostSummary } from "./PlanRevisionCostSummary";
import { ProviderProgressPanel } from "./ProviderProgressPanel";
import {
  CallbackInfoPanel,
  ProviderProgressEmptyState,
  TaskLogsPanel,
} from "./taskDetails";
import { UsageBreakdownSection } from "./UsageBreakdownSection";

interface TaskDetailsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  snapshot: TaskSnapshot;
  details: TaskDetailsResult;
  logs: TaskLogsResult;
  providerProgressExpanded?: boolean;
  onProviderProgressExpandedChange?: (expanded: boolean) => void;
}

export const TaskDetailsSheet: FC<TaskDetailsSheetProps> = ({
  isOpen,
  onClose,
  snapshot,
  details,
  logs,
  providerProgressExpanded,
  onProviderProgressExpandedChange,
}) => {
  const {
    getEventsForTask,
    getStreamConfigsForTask,
    getHydrationStatus,
    hydrateTask,
  } = useProviderProgress();
  const providerEvents = getEventsForTask(snapshot.task_id);
  const streamConfigs = getStreamConfigsForTask(snapshot.task_id);
  const hydrationStatus = getHydrationStatus(snapshot.task_id);
  const usageSummary = snapshot.usage_summary ?? null;
  const isTaskRunning =
    snapshot.status === "running" || snapshot.status === "queued";
  const realtimeUsage = useRealtimeTaskUsage(
    snapshot.task_id,
    usageSummary,
    isTaskRunning,
  );
  const planRevisionId = (() => {
    const { metadata } = snapshot;
    if (typeof metadata.revision_id === "string") {
      return metadata.revision_id;
    }
    const { flow } = metadata;
    if (flow && typeof flow === "object" && !Array.isArray(flow)) {
      const flowRecord = flow as Record<string, unknown>;
      if (typeof flowRecord.revision_id === "string") {
        return flowRecord.revision_id;
      }
    }
    return null;
  })();
  const {
    loading: schemaScopeLoading,
    error: schemaScopeError,
    data: schemaScopeSummary,
  } = useSchemaTelemetryScopeSummary(
    planRevisionId ? "plan_revision" : null,
    planRevisionId,
    Boolean(planRevisionId),
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    void hydrateTask(snapshot.task_id);
  }, [hydrateTask, isOpen, snapshot.task_id]);

  return (
    <SideSheet
      isOpen={isOpen}
      onClose={onClose}
      title={snapshot.task_id}
      subtitle={
        <>
          <StatusBadge
            status={snapshot.status}
            label={STATUS_LABELS[snapshot.status]}
            size="sm"
          />
          <Text>Updated {formatRelativeTime(snapshot.updated_at)}</Text>
        </>
      }
    >
      <div className="space-y-6">
        {/* Live Usage Metrics */}
        <LiveUsageMetrics usage={realtimeUsage} isTaskRunning={isTaskRunning} />

        {details.loading && (
          <LoadingState message="Loading task details..." className="py-8" />
        )}

        {details.error && (
          <Alert
            variant="error"
            action={
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={details.reload}
                className="border-rose-400/50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/60 dark:text-rose-200 dark:hover:bg-rose-500/20"
              >
                Retry
              </Button>
            }
          >
            Failed to load task details: {details.error}
          </Alert>
        )}

        {snapshot.status === "failed" && (
          <ValidationFailureSection task={snapshot} />
        )}

        {snapshot.last_callback && (
          <CallbackInfoPanel callback={snapshot.last_callback} />
        )}

        {providerEvents.length > 0 ? (
          <ProviderProgressPanel
            events={providerEvents}
            streamConfigs={streamConfigs}
            expanded={providerProgressExpanded}
            onExpandedChange={onProviderProgressExpandedChange}
          />
        ) : (
          <ProviderProgressEmptyState
            hydrationStatus={hydrationStatus}
            isSheetOpen={isOpen}
            taskId={snapshot.task_id}
            onRetry={() => {
              void hydrateTask(snapshot.task_id);
            }}
          />
        )}

        {usageSummary && <UsageBreakdownSection usageSummary={usageSummary} />}

        {planRevisionId && (
          <PlanRevisionCostSummary
            summary={schemaScopeSummary}
            loading={schemaScopeLoading}
            error={schemaScopeError}
            scopeId={planRevisionId}
          />
        )}

        <TaskLogsPanel logs={logs} />
        {logs.error && !logs.loading && (
          <Alert
            variant="error"
            action={
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={logs.reload}
                className="border-rose-400/50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/60 dark:text-rose-200 dark:hover:bg-rose-500/20"
              >
                Retry
              </Button>
            }
          >
            {logs.error}
          </Alert>
        )}
      </div>
    </SideSheet>
  );
};
